import { createSelector } from '@reduxjs/toolkit';
import { Analytics, DataStatus, Monitoring } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import * as SessionAPI from '../api/sessionAPI';
import type { LoginParams } from '../api/types';
import type { OrganizationRoleObject, Profile } from './types';
import {
  ViewAccessType,
  ContractType,
  OrganizationDataStatus,
  RequiredActionType,
} from './types';

interface State {
  profile?: Profile;
  status: DataStatus;
  showPaymentOvedueBanner: boolean;
  activeOrganizationId?: string;
}

const initialState: State = {
  status: DataStatus.idle,
  showPaymentOvedueBanner: false,
  activeOrganizationId: undefined,
};

export const sessionSlice = createAppSlice({
  name: 'session',
  initialState,
  reducers: (create) => ({
    setupProfile: create.asyncThunk(
      (_: void) => {
        return SessionAPI.getProfile();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.profile = action.payload;

          const storedOrganizationId = SessionAPI.getActiveOrganizationId();
          const isStoredIdValid =
            storedOrganizationId &&
            action.payload.organizations.some(
              (org) => org.id === storedOrganizationId,
            );

          state.activeOrganizationId = isStoredIdValid
            ? storedOrganizationId
            : action.payload.defaultOrganizationId ||
              action.payload.organizations[0]?.id;

          SessionAPI.setActiveOrganizationId(state.activeOrganizationId);

          Monitoring.setUser({
            id: action.payload.id,
            email: action.payload.email,
          });
          Analytics.setUserId(action.payload.analyticsId);

          if (
            action.payload.organizations.find(
              (org) => org.id === state.activeOrganizationId,
            )?.paymentOverdue
          ) {
            state.showPaymentOvedueBanner = true;
          }
        },
        settled: (state) => {
          state.status = DataStatus.finished;
        },
      },
    ),
    login: create.asyncThunk(
      (params: LoginParams, { dispatch }) => {
        if (params.shouldRemember) {
          SessionAPI.updateRememberMe(
            (params.credentials as { email: string }).email,
          );
        }

        return SessionAPI.login(params.credentials).then(async () => {
          await dispatch(setupProfile());
        });
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        settled: (state) => {
          state.status = DataStatus.finished;
        },
      },
    ),
    logout: create.asyncThunk(
      (_: void) => {
        SessionAPI.deleteRememberMe();
        SessionAPI.deleteShowPaymentOverdueBanner();
        SessionAPI.deleteActiveOrganizationId();

        return SessionAPI.logout();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.profile = undefined;
          state.activeOrganizationId = undefined;
        },
        settled: (state) => {
          state.status = DataStatus.idle;
        },
      },
    ),
    hidePaymentOverdueBanner: create.reducer((state) => {
      state.showPaymentOvedueBanner = false;
    }),
    updateRole: create.reducer<OrganizationRoleObject>((state, action) => {
      if (!state.profile || !state.activeOrganizationId) {
        return;
      }

      const orgIndex = state.profile.organizations.findIndex(
        (org) => org.id === state.activeOrganizationId,
      );

      if (orgIndex !== -1) {
        state.profile.organizations[orgIndex].role = action.payload;
      }
    }),
    setActiveOrganizationId: create.reducer<string>((state, action) => {
      state.activeOrganizationId = action.payload;
      SessionAPI.setActiveOrganizationId(action.payload);
    }),
    updateDefaultOrganizationId: create.asyncThunk(
      (defaultOrganizationId: string) =>
        SessionAPI.updateDefaultOrganizationId(defaultOrganizationId),
      {
        fulfilled: (state, action) => {
          if (!state.profile) {
            return;
          }

          state.profile.defaultOrganizationId = action.meta.arg;
        },
        settled: (state) => {
          state.status = DataStatus.finished;
        },
      },
    ),
    completeOnboarding: create.asyncThunk(
      (_: void) =>
        SessionAPI.deleteRequiredAction(RequiredActionType.COMPLETE_ONBOARDING),
      {
        fulfilled: (state) => {
          if (state.profile) {
            state.profile.requiredActions =
              state.profile.requiredActions.filter(
                (a) => a.type !== RequiredActionType.COMPLETE_ONBOARDING,
              );
          }
        },
      },
    ),
  }),
  selectors: {
    selectIsSessionRequested: (session) => {
      return session.status !== DataStatus.idle;
    },
    selectIsSessionLoading: (session) => {
      return session.status === DataStatus.loading;
    },
    selectIsAuthorized: (session) => {
      return session.status === DataStatus.finished && !!session.profile;
    },
    selectActiveOrganizationId: (session) => {
      return session.activeOrganizationId!;
    },
    selectOrganization: (session) => {
      return session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );
    },
    selectIsDisabled: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return !!session.profile && !organization?.isActive;
    },
    selectIsAdmin: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return (
        organization?.role.rules?.capabilities?.userManagement !==
        ViewAccessType.DENIED
      );
    },
    selectIsMcpAccessDenied: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return (
        organization?.role.rules?.capabilities?.mcpAccess ===
        ViewAccessType.DENIED
      );
    },
    selectUserId: (session) => {
      return session.profile?.id;
    },
    selectEmail: (session) => {
      return session.profile?.email;
    },
    selectShowPaymentOverdueBanner: (session) => {
      return session.showPaymentOvedueBanner;
    },
    selectOrganizationId: (session) => {
      return session.activeOrganizationId;
    },
    selectOrganizationName: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return organization?.name;
    },
    selectTimezone: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return organization?.timezone;
    },
    selectWebhookHash: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return organization?.webhookHash;
    },
    selectCurrency: createSelector(
      (state: State) => {
        const organization = state.profile?.organizations.find(
          (org) => org.id === state.activeOrganizationId,
        );

        return organization?.currency;
      },
      (currency) => {
        const validCurrency = isValidCurrency(currency) ? currency! : 'USD';

        return validCurrency;
      },
    ),
    selectViewsRestrictions: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return organization?.role.rules?.viewsRestrictions;
    },
    selectIsAddProductsAvailable: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return (
        organization?.contractType === ContractType.unlimitedProducts ||
        isCampaignswellUser(session.profile?.email)
      );
    },
    selectIsOrganizationDataPending: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return organization?.dataStatus === OrganizationDataStatus.PENDING;
    },
    selectIsOrganizationDataAccessible: (session) => {
      const organization = session.profile?.organizations.find(
        (org) => org.id === session.activeOrganizationId,
      );

      return (
        organization?.dataStatus === OrganizationDataStatus.READY ||
        isCampaignswellUser(session.profile?.email)
      );
    },
    selectOrganizations: (session) => {
      return session.profile?.organizations || [];
    },
    selectDefaultOrganizationId: (session) => {
      return session.profile?.defaultOrganizationId;
    },
    selectRequiresOnboarding: (session) => {
      return !!session.profile?.requiredActions.some(
        (a) => a.type === 'COMPLETE_ONBOARDING',
      );
    },
  },
});

const isCampaignswellUser = (email?: string) => {
  return email?.endsWith('@campaignswell.com');
};

const isValidCurrency = (currency: string | undefined): boolean => {
  // ISO 4217 currency codes are 3 uppercase letters
  const currencyRegex = /^[A-Z]{3}$/;

  return !!currency && currencyRegex.test(currency);
};

export const signInWithGoogle = () => {
  SessionAPI.signInWithGoogle();
};

export const hidePaymentOverdueBanner = (): AppThunk => (dispatch) => {
  dispatch(sessionSlice.actions.hidePaymentOverdueBanner());
  SessionAPI.hidePaymentOverdueBanner();
};

export const {
  login,
  logout,
  setupProfile,
  updateRole,
  setActiveOrganizationId,
  updateDefaultOrganizationId,
  completeOnboarding,
} = sessionSlice.actions;
export const {
  selectIsSessionRequested,
  selectIsSessionLoading,
  selectIsAuthorized,
  selectEmail,
  selectOrganizationId,
  selectWebhookHash,
  selectShowPaymentOverdueBanner,
  selectCurrency,
  selectIsAdmin,
  selectIsMcpAccessDenied,
  selectUserId,
  selectIsDisabled,
  selectViewsRestrictions,
  selectIsAddProductsAvailable,
  selectIsOrganizationDataPending,
  selectIsOrganizationDataAccessible,
  selectOrganizationName,
  selectTimezone,
  selectOrganization,
  selectActiveOrganizationId,
  selectOrganizations,
  selectDefaultOrganizationId,
  selectRequiresOnboarding,
} = sessionSlice.selectors;
